import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, tap } from 'rxjs';

import { ApiUrlService } from './api-url.service';
import {
  PresupuestoDetalle,
  PresupuestoHistorialResponse,
  PresupuestoListadoItem,
  PresupuestosListadoResponse
} from '../models/presupuestos';

@Injectable({
  providedIn: 'root'
})
export class AdminBudgetsService {
  private readonly basePath = '/api/v1/admin/budgets';
  private readonly pendientesRefreshSubject = new Subject<PresupuestoListadoItem[] | null>();
  readonly pendientesRefresh$ = this.pendientesRefreshSubject.asObservable();

  constructor(private apiUrl: ApiUrlService, private http: HttpClient) {}

  private resolveIdUsuario(source: Partial<PresupuestoDetalle> & Record<string, any>): string | null | undefined {
    return source?.id_usuario
      ?? source?.['idusuario']
      ?? source?.['idUsuario']
      ?? source?.['presupuesto']?.['id_usuario']
      ?? source?.['presupuesto']?.['idusuario']
      ?? source?.['presupuesto']?.['idUsuario'];
  }

  private normalizeUpdatePayload(
    idPresupuesto: number,
    presupuesto: Partial<PresupuestoDetalle> & Record<string, any>
  ): Record<string, any> {
    return {
      ...presupuesto,
      id_solicitud: presupuesto?.id_solicitud ?? presupuesto?.['id'] ?? idPresupuesto,
      id_usuario: this.resolveIdUsuario(presupuesto),
      fecha_creacion: presupuesto?.fecha_creacion ?? presupuesto?.['createdAt'] ?? presupuesto?.['created_at'],
      fecha_actualizacion: presupuesto?.fecha_actualizacion ?? presupuesto?.['updatedAt'] ?? presupuesto?.['updated_at']
    };
  }

  public notificarPendientesRefresh(items?: PresupuestoListadoItem[]): void {
    this.pendientesRefreshSubject.next(items ?? null);
  }

  public obtenerListadoPresupuestos(): Observable<PresupuestosListadoResponse> {
    return this.http.get<PresupuestosListadoResponse>(this.apiUrl.build(this.basePath));
  }

  public obtenerDetallePresupuesto(idPresupuesto: number): Observable<PresupuestoDetalle> {
    return this.http.get<PresupuestoDetalle>(this.apiUrl.build(`${this.basePath}/${idPresupuesto}`));
  }

  public actualizarDetallePresupuesto(idPresupuesto: number, presupuesto: Partial<PresupuestoDetalle>): Observable<PresupuestoDetalle> {
    const payload = this.normalizeUpdatePayload(idPresupuesto, presupuesto);
    return this.http.put<PresupuestoDetalle>(this.apiUrl.build(`${this.basePath}/${idPresupuesto}`), payload)
      .pipe(tap(() => this.notificarPendientesRefresh()));
  }

  public actualizarEstadoPresupuesto(idPresupuesto: number, estado: string, comentario?: string, idUsuario?: string | null): Observable<any> {
    const payload: Record<string, any> = { estado };
    const comentarioNormalizado = comentario?.trim();
    if (comentarioNormalizado) payload['comentario'] = comentarioNormalizado;
    if (idUsuario) payload['id_usuario'] = idUsuario;
    return this.http.put(this.apiUrl.build(`${this.basePath}/${idPresupuesto}/status`), payload)
      .pipe(tap(() => this.notificarPendientesRefresh()));
  }

  public obtenerHistorialPresupuesto(idPresupuesto: number): Observable<PresupuestoHistorialResponse> {
    return this.http.get<PresupuestoHistorialResponse>(this.apiUrl.build(`${this.basePath}/${idPresupuesto}/history`));
  }

  public obtenerImagenPresupuesto(idPresupuesto: number | string, cacheBuster?: number): Observable<Blob> {
    const url = this.apiUrl.build(`${this.basePath}/${idPresupuesto}/attachment`);
    const options = cacheBuster
      ? { params: { t: String(cacheBuster) }, responseType: 'blob' as const }
      : { responseType: 'blob' as const };
    return this.http.get(url, options);
  }

  public subirImagenPresupuesto(idPresupuesto: number | string, imagen: File): Observable<{ message?: string }> {
    const payload = new FormData();
    payload.append('imagen', imagen, imagen.name);
    return this.http.post<{ message?: string }>(
      this.apiUrl.build(`${this.basePath}/${idPresupuesto}/attachment`),
      payload
    );
  }

  public eliminarImagenPresupuesto(idPresupuesto: number | string): Observable<{ message?: string }> {
    return this.http.delete<{ message?: string }>(this.apiUrl.build(`${this.basePath}/${idPresupuesto}/attachment`));
  }
}
